/**
 * Place slug utilities — romanized, locale-independent path slugs for the search
 * URL scheme (`/{locale}/s/{slug}?scope=...`).
 *
 * WHY: the old scheme put the *localized* region name in the `[region]` path
 * (`/s/日本`, `/s/서울`) and reverse-mapped it back to a place, which is fragile
 * (caused the "국가.국가" bug) and produces locale-fragmented URLs. Following the
 * Booking.com / TripAdvisor convention, the path slug is a stable romanized
 * identifier (`japan`, `seoul`), the ISO code lives in `?country=`, and `?scope=`
 * is the authoritative discriminator. Locale stays in the `/[locale]/` prefix.
 *
 * All indexes are derived once from COUNTRIES/CITIES config (each has `name.en`).
 */

import {
  COUNTRIES,
  CITIES,
  getCityBounds,
  type Bounds,
  type City,
  type Country,
} from "@/config/locations";
import { placeSlug } from "./slugify";
import type { Locale } from "@/i18n/routing";

export type SearchScope =
  | "city"
  | "district"
  | "region"
  | "country"
  | "nearby"
  | "keyword"
  | "partner";

/** Reserved slugs that are not places. */
export const KEYWORD_SLUG = "products";
export const NEARBY_SLUG = "nearby";

/** Romanize an English name into a stable kebab-case slug. "New York" → "new-york". */
export { placeSlug };

export const countrySlug = (c: Country): string => placeSlug(c.name.en);
export const citySlug = (c: City): string => placeSlug(c.name.en);

// ----- Reverse indexes (built once at module load) -----

const slugToCountry = new Map<string, Country>();
for (const c of COUNTRIES) slugToCountry.set(countrySlug(c), c);

const codeToCountry = new Map<string, Country>();
for (const c of COUNTRIES) codeToCountry.set(c.code, c);

// citySlug can collide across countries (e.g. two "Springfield") → key by
// `${slug}` but keep a list; disambiguate by country when provided.
const slugToCities = new Map<string, City[]>();
for (const c of CITIES) {
  const s = citySlug(c);
  const arr = slugToCities.get(s);
  if (arr) arr.push(c);
  else slugToCities.set(s, [c]);
}

/** Any localized name (city or country) → matching City/Country (old-URL back-compat). */
function findByLocalizedName(name: string): { city?: City; country?: Country } {
  if (!name) return {};
  const lower = name.toLowerCase();
  const matches = (n: City["name"] | Country["name"]) =>
    Object.values(n).some(
      (v) => typeof v === "string" && (v === name || v.toLowerCase() === lower),
    );
  const city = CITIES.find((c) => matches(c.name));
  if (city) return { city };
  const country = COUNTRIES.find((c) => matches(c.name));
  if (country) return { country };
  return {};
}

/** Localized city name (any of 5 langs) → City. Used by SearchBar navigation. */
export function cityByAnyName(name: string): City | undefined {
  return findByLocalizedName(name).city;
}

/** Localized country name (any of 5 langs) → Country. */
export function countryByAnyName(name: string): Country | undefined {
  return findByLocalizedName(name).country;
}

export function countryBySlug(slug: string): Country | undefined {
  return slugToCountry.get(slug);
}

export function countryByCode(code: string): Country | undefined {
  return codeToCountry.get(code.toUpperCase());
}

/** Resolve a city slug, disambiguating by ISO country code when the slug collides. */
export function cityBySlug(slug: string, countryCode?: string): City | undefined {
  const arr = slugToCities.get(slug);
  if (!arr || arr.length === 0) return undefined;
  if (arr.length === 1 || !countryCode) return arr[0];
  return arr.find((c) => c.countryCode === countryCode.toUpperCase()) ?? arr[0];
}

// ----- Canonical descriptor + legacy resolution (301) -----

export interface CanonicalPlace {
  scope: SearchScope;
  slug: string;
  country?: string; // ISO code
}

/**
 * Decide the canonical (scope, slug, country) for an *old-format* inbound URL so
 * page.tsx can 301-redirect to it. Returns null when the URL is already canonical
 * (has a `scope` param) — no redirect needed.
 *
 * Inputs are the raw `[region]` segment (already decoded) and the current
 * searchParams (a plain record of the values we care about).
 */
export function resolveLegacyRegion(
  decodedRegion: string,
  params: {
    scope?: string;
    delivery_country?: string;
    search?: string;
    location_search?: string;
  },
): CanonicalPlace | null {
  // Already canonical — the new scheme always carries `scope`.
  if (params.scope) return null;

  // Nearby.
  if (decodedRegion === NEARBY_SLUG) {
    return { scope: "nearby", slug: NEARBY_SLUG };
  }

  // Keyword / country-wide "products" bucket.
  if (decodedRegion === KEYWORD_SLUG) {
    if (params.delivery_country) {
      const country = countryByCode(params.delivery_country);
      return {
        scope: "country",
        slug: country ? countrySlug(country) : KEYWORD_SLUG,
        country: params.delivery_country.toUpperCase(),
      };
    }
    return { scope: "keyword", slug: KEYWORD_SLUG };
  }

  // Region is a place name. Old URLs used localized names; new URLs use romanized
  // slugs. Match either form.
  const bySlugCity = cityBySlug(decodedRegion, params.delivery_country);
  const bySlugCountry = countryBySlug(decodedRegion);
  const byLocalized = findByLocalizedName(decodedRegion);

  const country = bySlugCountry ?? byLocalized.country;
  const city = bySlugCity ?? byLocalized.city;

  // Country-only (e.g. old `/s/日本?delivery_country=JP`, or romanized `/s/japan`).
  if (country && !city) {
    return { scope: "country", slug: countrySlug(country), country: country.code };
  }

  if (city) {
    return {
      scope: "city",
      slug: citySlug(city),
      country: city.countryCode,
    };
  }

  // Unknown region string → treat as a keyword search fallback.
  return { scope: "keyword", slug: KEYWORD_SLUG };
}

/**
 * For a canonical (scope) request, resolve the place details a loader needs.
 * `slug` is the romanized path; `country` the ISO param.
 */
export interface ResolvedScope {
  scope: SearchScope;
  country?: Country;
  city?: City;
  bounds?: Bounds | null;
}

export function resolveScope(
  scope: SearchScope,
  slug: string,
  countryCode: string | undefined,
  locale: Locale,
): ResolvedScope {
  if (scope === "country") {
    const country =
      (countryCode ? countryByCode(countryCode) : undefined) ??
      countryBySlug(slug);
    return { scope, country };
  }
  if (scope === "city" || scope === "district") {
    const city = cityBySlug(slug, countryCode);
    const country = countryCode
      ? countryByCode(countryCode)
      : city
        ? countryByCode(city.countryCode)
        : undefined;
    const bounds = city ? getCityBounds(city.name.en, "en") : null;
    return { scope, country, city, bounds };
  }
  return { scope };
}
